import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthState } from '@/components/feature/AuthGuard';
import { getMyAdmScopes } from '@/pages/adm/lib/admApi';
import type { AdmScopes } from '@/pages/adm/lib/admApi';

export interface AdmAuth {
  userId: string;
  email: string | undefined;
  scopes: AdmScopes;
  isRoot: boolean;
  signOut: () => Promise<void>;
}

export default function AdmGate({
  requiredScope,
  children,
}: {
  requiredScope: 'root' | 'rg' | 'og';
  children: (auth: AdmAuth) => ReactNode;
}) {
  const auth = useAuthState();
  const location = useLocation();
  const [scopes, setScopes] = useState<AdmScopes | null>(null);
  const [loadingScopes, setLoadingScopes] = useState(false);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);

  const userId = auth.user?.id ?? null;

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoadingScopes(true);
    setScopeError(null);
    getMyAdmScopes()
      .then((s) => {
        if (!cancelled) setScopes(s);
      })
      .catch((e) => {
        if (!cancelled) setScopeError(e instanceof Error ? e.message : '讀取授權失敗');
      })
      .finally(() => {
        if (!cancelled) setLoadingScopes(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, retry]);

  if (auth.loading || (userId && loadingScopes)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
          <p className="text-sm text-foreground-500">載入授權中……</p>
        </div>
      </div>
    );
  }

  if (!auth.user) {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/account?returnTo=${returnTo}`} replace />;
  }

  if (scopeError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center space-y-3 max-w-sm px-4">
          <p className="text-sm text-rose-600">{scopeError}</p>
          <button
            onClick={() => setRetry((r) => r + 1)}
            className="whitespace-nowrap px-4 py-2 rounded-md border border-foreground-200 text-sm text-foreground-700 cursor-pointer"
          >
            重試
          </button>
        </div>
      </div>
    );
  }

  const allowed = scopes
    ? requiredScope === 'root'
      ? scopes.is_root
      : scopes.is_root || (requiredScope === 'rg' ? scopes.has_rg : scopes.has_og)
    : false;

  if (!allowed) {
    return <AdmAccessDenied requiredScope={requiredScope} />;
  }

  return (
    <>
      {children({
        userId: auth.user.id,
        email: auth.user.email,
        scopes: scopes as AdmScopes,
        isRoot: (scopes as AdmScopes).is_root,
        signOut: auth.signOut,
      })}
    </>
  );
}

function AdmAccessDenied({ requiredScope }: { requiredScope: 'root' | 'rg' | 'og' }) {
  const label =
    requiredScope === 'root'
      ? 'ROOT ADM（總管理員）'
      : requiredScope === 'rg'
        ? 'ADM for RG（RG 領域管理）'
        : 'ADM for OG（OG 領域管理）';
  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50">
      <div className="text-center space-y-4 max-w-sm px-4">
        <div className="w-16 h-16 mx-auto rounded-full bg-foreground-100 flex items-center justify-center">
          <i className="ri-shield-user-line text-foreground-400 text-2xl" />
        </div>
        <h1 className="text-lg text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
          需要 {label} 權限
        </h1>
        <p className="text-sm text-foreground-500">
          你目前的授權範圍無法進入此頁面。若需要此權限，請聯繫 ROOT ADM 授予。
        </p>
      </div>
    </div>
  );
}